import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useRouter } from 'expo-router';
import { View, TouchableOpacity, Animated, TextInput, Modal, FlatList } from 'react-native';
import { Image } from 'expo-image';
import { Text } from '@/components';
import LottieLoader from '@/components/LottieLoader';
import TrailMap from '@/components/TrailMap';
import TrailblazerHero from '@/assets/images/trailblazer-hero.svg';
import MapLegendPaw from '../../assets/images/map-icon.svg';
import TrailMapPopup from '@/components/TrailMapPopup';
import ExploreSearchBar from '@/components/ExploreSearchBar';
import FilterSheet from '@/components/FilterSheet';
import { logEvent } from '@/lib/analytics';
import FilterPillRow from '@/components/FilterPillRow';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import * as Haptics from 'expo-haptics';
import { MapPin, Umbrella, Star, X, Navigation, ChevronLeft, Trees, TrafficCone, Mountain, List, Map } from 'lucide-react-native';
import theme from '@/constants/colors';
import { Typography } from '@/constants/typography';
import { useSaveTrailBookmark, useRemoveTrailBookmark, useIsTrailSaved } from '@/lib/queries/trails';
import { Trail } from '@/types/trail';
import TrailCard from '@/components/TrailCard';
import { useExploreFilters, DEFAULT_FILTERS } from '@/hooks/useExploreFilters';
import { useUserLocation } from '@/hooks/useUserLocation';
import { useExploreTrails } from '@/lib/queries/explore';
import { searchLocations, GeocodeResult } from '@/lib/geocode';
import { useAuth } from '@/context/AuthContext';
import { useSelectedDog } from '@/context/SelectedDogContext';
import DogPicker from '@/components/DogPicker';
import styles from './explore.styles';
import { useTranslation } from 'react-i18next';

export default function ExploreScreen() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();
  const { session } = useAuth();
  const token = session?.accessToken;
  const { dogId } = useSelectedDog();
  // dogId is part of the useExploreTrails query key, so switching dogs
  // automatically refetches with the new match scores — no manual invalidation.

  // ─── Bookmark mutations & helper ────────────────────────────────
  const saveTrailBookmarkMutation = useSaveTrailBookmark(token);
  const removeTrailBookmarkMutation = useRemoveTrailBookmark(token);
  const isTrailSaved = useIsTrailSaved(token);

  const saveTrailBookmark = useCallback(async (trailId: string) => saveTrailBookmarkMutation.mutateAsync(trailId), [saveTrailBookmarkMutation]);
  const removeTrailBookmark = useCallback(async (trailId: string) => removeTrailBookmarkMutation.mutateAsync(trailId), [removeTrailBookmarkMutation]);

  // ─── Filter state ────────────────────────────────────────────
  const { filters, setFilters, updateFilter, toggleTag, clearAll, activeFilterCount, hasActiveFilters, buildQueryParams, setLocation, clearLocation, setMapCenter } = useExploreFilters();

  // ─── Location (React Query–friendly hook) ─────────────────────
  const { locationCoords, locationLabel: currentLocation, isLoading: isLocationLoading } = useUserLocation();

  // ─── View state ──────────────────────────────────────────────
  const [selectedCategory, setSelectedCategory] = useState<string>('for-you');
  const [bookmarkAnimations, setBookmarkAnimations] = useState<Record<string, Animated.Value>>({});
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showSearchModal, setShowSearchModal] = useState<boolean>(false);
  const [showFilterSheet, setShowFilterSheet] = useState<boolean>(false);
  const [geocodeResults, setGeocodeResults] = useState<GeocodeResult[]>([]);
  const [geocodeError, setGeocodeError] = useState<string | null>(null);
  const [activeImageIndex, setActiveImageIndex] = useState<Record<string, number>>({});
  const [isCardSwiping, setIsCardSwiping] = useState<Record<string, boolean>>({});
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [selectedMapTrailId, setSelectedMapTrailId] = useState<string | null>(null);
  const mapRegionRef = useRef<{ latitude: number; longitude: number; latitudeDelta: number; longitudeDelta: number } | null>(null);
  const mapDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const trailMapRef = useRef<any>(null);
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const hasAnimatedToInitialLocationRef = useRef(false);

  // ─── Trails data via React Query ───────────────────────────────
  const exploreQuery = useExploreTrails({
    token,
    selectedCategory,
    locationCoords,
    filters,
    hasActiveFilters,
    buildQueryParams,
    dogId,
  });

  const trails = useMemo(() => exploreQuery.data?.pages.flatMap((p) => p.trails) ?? [], [exploreQuery.data]);
  // Full pre-pagination count from the first page's `total` (same on every page).
  const totalTrails = exploreQuery.data?.pages[0]?.total ?? trails.length;

  // Internal analytics: log one search_performed per applied filter/search change.
  // The initial load is a browse, not a search — it only sets the baseline.
  // Coordinates included to build the per-town coverage metric later.
  const lastSearchKeyRef = useRef<string | null>(null);
  const filterKey = useMemo(() => JSON.stringify(filters), [filters]);
  useEffect(() => {
    const firstPage = exploreQuery.data?.pages?.[0];
    if (!firstPage) return;
    if (lastSearchKeyRef.current === null) {
      lastSearchKeyRef.current = filterKey;
      return;
    }
    if (filterKey === lastSearchKeyRef.current) return;
    lastSearchKeyRef.current = filterKey;
    logEvent('search_performed', {
      result_count: firstPage.total ?? trails.length,
      search_location_name: filters.searchLocationName ?? null,
      search_latitude: filters.searchLatitude ?? null,
      search_longitude: filters.searchLongitude ?? null,
      active_filters: activeFilterCount,
    });
  }, [filterKey, exploreQuery.data, activeFilterCount, filters.searchLocationName, filters.searchLatitude, filters.searchLongitude, trails.length]);
  const isLoadingNearby = exploreQuery.isLoading || isLocationLoading;
  const exploreError = exploreQuery.error;
  const isExploreError = exploreQuery.isError;
  const hasNextPage = exploreQuery.hasNextPage;
  const isFetchingNextPage = exploreQuery.isFetchingNextPage;
  const fetchNextPage = exploreQuery.fetchNextPage;

  // ─── Map pan state (unified with list query) ───────────────
  // Removed: a separate mapTrails fetch. Map panning now updates the shared
  // filter-based query source, so list and map always show the same results.

  const isLoadingMap = exploreQuery.isFetching && !exploreQuery.isFetchingNextPage;

  // ─── Search suggestions (local + geocode) ────────────────────
  const [searchSuggestions, setSearchSuggestions] = useState<{ id: string; name: string; location: string; type: string }[]>([]);

  const handleSearchQueryChange = useCallback((query: string) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchSuggestions([]);
      setGeocodeResults([]);
      return;
    }

    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(async () => {
      const q = query.toLowerCase();
      const localSuggestions = trails
        .filter(trail => {
          const name = trail.name?.toLowerCase() || '';
          const city = trail.city?.toLowerCase() || '';
          const country = trail.country?.toLowerCase() || '';
          return name.includes(q) || city.includes(q) || country.includes(q);
        })
        .map(trail => ({
          id: trail.id,
          name: trail.name || `Trail ${new Date(trail.date).toLocaleDateString()}`,
          location: trail.city ? `${trail.city}, ${trail.country || t('trailDetail.unknown')}` : t('explore.locationUnavailable'),
          type: 'trail' as const,
        }))
        .slice(0, 4);

      setSearchSuggestions(localSuggestions);

      try {
        setGeocodeError(null);
        const locations = await searchLocations(query, 3);
        setGeocodeResults(locations);
      } catch {
        setGeocodeResults([]);
        setGeocodeError(t('explore.locationSearchUnavailable'));
      }
    }, 300);
  }, [trails, t]);

  // ─── Handlers ────────────────────────────────────────────────
  const handleSearchBarPress = () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setShowSearchModal(true);
  };

  const handleFilterPress = () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setShowFilterSheet(true);
  };

  const handleFilterApply = (newFilters: typeof filters) => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    setFilters(newFilters);
  };

  const handleRemoveFilter = (key: keyof typeof filters, value?: any) => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    if (key === 'tags' && value) {
      toggleTag(value);
    } else if (key === 'minDistance' || key === 'maxDistance') {
      setFilters(prev => ({ ...prev, minDistance: null, maxDistance: null }));
    } else if (key === 'minUserDistance' || key === 'maxUserDistance') {
      setFilters(prev => ({ ...prev, minUserDistance: null, maxUserDistance: null }));
    } else if (key === 'searchLocationName') {
      clearLocation();
    } else {
      const defaultValue = (DEFAULT_FILTERS as any)[key];
      setFilters(prev => ({ ...prev, [key]: defaultValue }));
    }
  };

  const handleCategoryPress = (categoryId: string) => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    if (categoryId === 'for-you') {
      setFilters(prev => ({ ...prev, sort: 'match_score', tags: [] }));
    } else if (categoryId === 'nearby') {
      setFilters(prev => ({ ...prev, sort: 'distance', tags: [] }));
    } else {
      setFilters(prev => ({ ...prev, tags: [categoryId] }));
    }
    setSelectedCategory(categoryId);
  };

  const handleCloseSearchModal = () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setShowSearchModal(false);
    setSearchQuery('');
    setSearchSuggestions([]);
    setGeocodeResults([]);
    setGeocodeError(null);
  };

  const handleSuggestionPress = (suggestionId: string) => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setShowSearchModal(false);
    setSearchQuery('');
    setSearchSuggestions([]);
    setGeocodeResults([]);
    setGeocodeError(null);
    router.push(`/trail/${suggestionId}`);
  };

  const handleGeocodeResultPress = (result: GeocodeResult) => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    setLocation(result.lat, result.lon, result.display_name);
    setShowSearchModal(false);
    setSearchQuery('');
    setSearchSuggestions([]);
    setGeocodeResults([]);
    setGeocodeError(null);
  };

  const handleCurrentLocationPress = () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    clearLocation();
    setShowSearchModal(false);
    setSearchQuery('');
    setSearchSuggestions([]);
    setGeocodeResults([]);
    setGeocodeError(null);
  };

  // ─── Bookmark handlers ──────────────────────────────────────
  const getBookmarkAnimation = (trailId: string) => {
    if (!bookmarkAnimations[trailId]) {
      const newAnim = new Animated.Value(1);
      setBookmarkAnimations(prev => ({ ...prev, [trailId]: newAnim }));
      return newAnim;
    }
    return bookmarkAnimations[trailId];
  };

  const isValidUUID = (id: string): boolean => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(id);
  };

  const handleBookmarkPress = async (trailId: string, isRealTrail: boolean = false) => {
    if (!isRealTrail && !isValidUUID(trailId)) {
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Warning);
      return;
    }
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);

    const anim = getBookmarkAnimation(trailId);
    const saved = isTrailSaved(trailId);

    Animated.sequence([
      Animated.timing(anim, { toValue: 0.6, duration: 100, useNativeDriver: true }),
      Animated.spring(anim, { toValue: 1, friction: 3, tension: 100, useNativeDriver: true }),
    ]).start();

    try {
      if (saved) {
        await removeTrailBookmark(trailId);
      } else {
        await saveTrailBookmark(trailId);
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
      }
    } catch (error) {
      console.error('Failed to toggle bookmark:', error);
    }
  };

  const handleNavigateToTrail = (trailId: string) => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    router.push(`/trail/${trailId}`);
  };

  // ─── Map ─────────────────────────────────────────────────────
  const displayTrails = trails;

  const mapMarkers = useMemo(() => {
    return displayTrails
      .filter((trail) => Number.isFinite(trail.startLatitude) && Number.isFinite(trail.startLongitude))
      .map((trail) => ({
        id: trail.id,
        latitude: trail.startLatitude as number,
        longitude: trail.startLongitude as number,
        label: Number.isFinite(trail.dogMatchScore) ? `${Math.round(trail.dogMatchScore)}%` : '-',
      }));
  }, [displayTrails]);

  const mapRegion = useMemo(() => {
    const lat = filters.mapCenterLatitude ?? filters.searchLatitude ?? locationCoords?.latitude ?? 40.7128;
    const lng = filters.mapCenterLongitude ?? filters.searchLongitude ?? locationCoords?.longitude ?? -74.006;
    const markers = mapMarkers;
    if (filters.mapCenterLatitude != null || filters.searchLatitude != null || !locationCoords || markers.length === 0) {
      return { latitude: lat, longitude: lng, latitudeDelta: 0.2, longitudeDelta: 0.2 };
    }
    const latitudes = markers.map((m) => m.latitude);
    const longitudes = markers.map((m) => m.longitude);
    return {
      latitude: (Math.min(...latitudes) + Math.max(...latitudes)) / 2,
      longitude: (Math.min(...longitudes) + Math.max(...longitudes)) / 2,
      latitudeDelta: Math.max((Math.max(...latitudes) - Math.min(...latitudes)) * 1.5, 0.03),
      longitudeDelta: Math.max((Math.max(...longitudes) - Math.min(...longitudes)) * 1.5, 0.03),
    };
  }, [mapMarkers, locationCoords, filters.mapCenterLatitude, filters.mapCenterLongitude, filters.searchLatitude, filters.searchLongitude]);

  const handleMapRegionChange = useCallback((region: { latitude: number; longitude: number; latitudeDelta: number; longitudeDelta: number }) => {
    // Ignore region events that are just the camera snapping to the current
    // query source (initial mount / programmatic animate). Only refetch when
    // the center has panned ~10km from where data was last loaded — small pans
    // and zooms don't move the center far enough to change the result set.
    // ponytail: flat degree threshold (~0.09° ≈ 10km); haversine if precision matters.
    const sourceLat = filters.mapCenterLatitude ?? filters.searchLatitude ?? locationCoords?.latitude ?? 40.7128;
    const sourceLng = filters.mapCenterLongitude ?? filters.searchLongitude ?? locationCoords?.longitude ?? -74.006;
    const PAN_THRESHOLD_DEG = 0.09; // ~10km
    const hasMoved =
      Math.abs(region.latitude - sourceLat) > PAN_THRESHOLD_DEG ||
      Math.abs(region.longitude - sourceLng) > PAN_THRESHOLD_DEG;
    if (!hasMoved) return;

    if (mapDebounceTimerRef.current) clearTimeout(mapDebounceTimerRef.current);
    mapRegionRef.current = region;
    mapDebounceTimerRef.current = setTimeout(() => {
      if (mapRegionRef.current) {
        setMapCenter(mapRegionRef.current.latitude, mapRegionRef.current.longitude);
      }
    }, 500);
  }, [setMapCenter, filters.mapCenterLatitude, filters.mapCenterLongitude, filters.searchLatitude, filters.searchLongitude, locationCoords]);

  // Animate map camera when the search location changes (geocode select)
  const prevSearchLat = useRef<number | null>(null);
  const prevSearchLng = useRef<number | null>(null);
  useEffect(() => {
    const lat = filters.searchLatitude;
    const lng = filters.searchLongitude;
    if (lat != null && lng != null && (lat !== prevSearchLat.current || lng !== prevSearchLng.current)) {
      prevSearchLat.current = lat;
      prevSearchLng.current = lng;
      // Animate map to the new search location after a short delay to let data load
      const timer = setTimeout(() => {
        if (trailMapRef.current && mapRegion) {
          trailMapRef.current.animateToRegion(mapRegion, 1000);
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [filters.searchLatitude, filters.searchLongitude, mapRegion]);

  // When the user's location first resolves and we aren't already looking at
  // a searched or panned location, centre the map on them.
  useEffect(() => {
    if (hasAnimatedToInitialLocationRef.current) return;
    if (!locationCoords) return;
    if (filters.mapCenterLatitude != null || filters.searchLatitude != null) return;
    if (!trailMapRef.current) return;
    hasAnimatedToInitialLocationRef.current = true;
    trailMapRef.current.animateToRegion({
      latitude: locationCoords.latitude,
      longitude: locationCoords.longitude,
      latitudeDelta: 0.05,
      longitudeDelta: 0.05,
    }, 500);
  }, [locationCoords, filters.mapCenterLatitude, filters.searchLatitude]);

  const handleViewToggle = () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setViewMode((prev) => (prev === 'list' ? 'map' : 'list'));
    setSelectedMapTrailId(null);
  };

  const selectedMapTrail = useMemo(
    () => displayTrails.find((t) => t.id === selectedMapTrailId) ?? null,
    [displayTrails, selectedMapTrailId]
  );

  const selectedMapTrailCoordinates = useMemo(() => {
    if (!selectedMapTrail) return [];
    if (Array.isArray(selectedMapTrail.coordinates) && selectedMapTrail.coordinates.length > 1) return selectedMapTrail.coordinates;
    if (Array.isArray(selectedMapTrail.path) && selectedMapTrail.path.length > 1) {
      return selectedMapTrail.path
        .filter((point) => Array.isArray(point) && point.length >= 2)
        .map((point) => ({ latitude: point[1], longitude: point[0] }));
    }
    return [];
  }, [selectedMapTrail]);

  // ─── Determine which category chip is active ─────────────────
  const effectiveCategory = useMemo(() => {
    if (hasActiveFilters) return 'filtered';
    return selectedCategory;
  }, [hasActiveFilters, selectedCategory]);

  // ─── Render ──────────────────────────────────────────────────
  const renderCategoryHeader = () => (
    <View style={[styles.stickyHeader, { paddingTop: insets.top + 16 }]}>
      {/* Search bar */}
      <ExploreSearchBar
        onSearchPress={handleSearchBarPress}
        onFilterPress={handleFilterPress}
        activeFilterCount={activeFilterCount}
        searchLocationName={filters.searchLocationName}
        onClearLocation={clearLocation}
      />

      {/* Active filter pills */}
      {hasActiveFilters && (
        <FilterPillRow
          filters={filters}
          onRemoveFilter={handleRemoveFilter}
          onClearAll={clearAll}
        />
      )}

      {/* Result count */}
      {!isLoadingNearby && totalTrails > 0 && (
        <View style={{ paddingHorizontal: 16, paddingTop: 8, paddingBottom: 4 }}>
          <Text style={{ ...Typography.caption(theme.textMuted), fontSize: 13 }}>
            {hasActiveFilters
              ? t('explore.trailsMatchFilters', { count: totalTrails })
              : filters.searchLocationName
                ? t('explore.trailsNearLocation', { count: totalTrails, location: filters.searchLocationName })
                : t('explore.trailsFound', { count: totalTrails })
            }
          </Text>
        </View>
      )}

      <DogPicker compact />
    </View>
  );

  return (
    <View style={styles.safeContainer}>
      {viewMode === 'list' ? (
        <FlatList
          style={styles.container}
          contentContainerStyle={styles.categoryTrailsSection}
          data={displayTrails}
          keyExtractor={(item) => item.id}
          windowSize={7}
          removeClippedSubviews
          scrollEnabled={!Object.values(isCardSwiping).some(Boolean)}
          showsVerticalScrollIndicator={false}
          stickyHeaderIndices={[0]}
          ListHeaderComponent={renderCategoryHeader}
          onEndReached={() => { if (hasNextPage && !isFetchingNextPage) fetchNextPage(); }}
          onEndReachedThreshold={0.5}
          renderItem={({ item: trail }) => (
            <TrailCard
              trail={trail}
              onPress={(id) => handleNavigateToTrail(id)}
              onBookmarkPress={(id) => handleBookmarkPress(id, isValidUUID(id))}
              isSaved={isTrailSaved(trail.id)}
              onSwipeStateChange={(id, swiping) => setIsCardSwiping(prev => ({ ...prev, [id]: swiping }))}
              hidePublicBadge
            />
          )}
          ListEmptyComponent={
            isLoadingNearby ? (
              <View style={styles.loadingContainer}>
                <LottieLoader size={120} />
              </View>
            ) : isExploreError ? (
              <View style={styles.emptyContainer}>
                <MapPin size={48} color={theme.textMuted} />
                <Text style={styles.emptyText}>
                  {t('explore.failedToLoadTrails')}
                </Text>
                <TouchableOpacity
                  onPress={() => exploreQuery.refetch()}
                  style={{ marginTop: 12, paddingHorizontal: 20, paddingVertical: 10, backgroundColor: theme.accentPrimary, borderRadius: 20 }}
                >
                  <Text style={{ color: theme.backgroundPrimary, fontWeight: '600' }}>{t('common.retry')}</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <View style={styles.emptyContainer}>
                {!hasActiveFilters && (
                  <TrailblazerHero width={265} height={126} style={{ marginBottom: 16 }} />
                )}
                {hasActiveFilters && <MapPin size={48} color={theme.textMuted} />}
                <Text style={styles.emptyText}>
                  {hasActiveFilters
                    ? t('explore.noTrailsMatchFilters')
                    : t('explore.noTrailsFound')
                  }
                </Text>
                {hasActiveFilters && (
                  <TouchableOpacity onPress={clearAll} style={{ marginTop: 12, paddingHorizontal: 20, paddingVertical: 10, backgroundColor: theme.accentPrimary, borderRadius: 20 }}>
                    <Text style={{ color: theme.backgroundPrimary, fontWeight: '600' }}>{t('filters.clearAll')}</Text>
                  </TouchableOpacity>
                )}
              </View>
            )
          }
          ListFooterComponent={
            <>
              {isFetchingNextPage && (
                <View style={{ paddingVertical: 24, alignItems: 'center' }}>
                  <LottieLoader size={40} compact />
                </View>
              )}
              <View style={styles.bottomPadding} />
            </>
          }
        />
      ) : (
        <View style={styles.container}>
          {renderCategoryHeader()}
          <View style={styles.mapViewContainer}>
            <View style={styles.mapCard}>
              <TrailMap
                ref={trailMapRef}
                style={{ flex: 1 }}
                initialRegion={mapRegion}
                coordinates={selectedMapTrailCoordinates}
                scrollEnabled
                zoomEnabled
                showStartMarker={false}
                showsUserLocation={!!locationCoords}
                userLocation={locationCoords ?? null}
                pointMarkers={mapMarkers}
                routeColor={theme.backgroundPrimary}
                routeWidth={5}
                routeOpacity={0.95}
                onPointMarkerPress={(id) => {
                  Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
                  setSelectedMapTrailId((prev) => (prev === id ? null : id));
                }}
                onRegionDidChange={handleMapRegionChange}
              />
              {mapMarkers.length === 0 && (
                <View style={styles.mapEmptyOverlay}>
                  <Text style={styles.mapEmptyText}>{t('explore.noTrailStartPoints')}</Text>
                </View>
              )}
              {/* Legend: the paw marker is the user's location (always shown when GPS is on),
                  distinct from trail-start pills — explains the marker & reconciles it with the
                  "no trail start points" banner above. */}
              <View style={styles.mapLegend}>
                <View style={styles.mapLegendRow}>
                  <MapLegendPaw style={styles.mapLegendPaw} />
                  <Text style={styles.mapLegendText}>{t('explore.legendYourLocation')}</Text>
                </View>
                <View style={styles.mapLegendRow}>
                  <View style={styles.mapLegendPill}>
                    <Text style={styles.mapLegendPillText}>%</Text>
                  </View>
                  <Text style={styles.mapLegendText}>{t('explore.legendTrailStart')}</Text>
                </View>
              </View>
              {isLoadingMap && (
                <View style={styles.mapLoadingPill}>
                  <LottieLoader size={22} compact />
                  <Text style={styles.mapLoadingText}>{t('explore.updatingTrails')}</Text>
                </View>
              )}
              <TrailMapPopup
                trail={selectedMapTrail}
                onClose={() => setSelectedMapTrailId(null)}
                onViewTrail={(id) => { setSelectedMapTrailId(null); handleNavigateToTrail(id); }}
              />
            </View>
          </View>
        </View>
      )}

      {/* Map/List toggle — the only one: the duplicate in the empty state is
          removed, so users can still explore the map with no trails */}
      <TouchableOpacity
        style={[styles.floatingToggleButton, { bottom: Math.max(insets.bottom + 12, 24) }]}
        onPress={handleViewToggle}
        activeOpacity={0.9}
      >
        {viewMode === 'list' ? (
          <Map size={20} color={theme.backgroundPrimary} strokeWidth={2.2} />
        ) : (
          <List size={20} color={theme.backgroundPrimary} strokeWidth={2.2} />
        )}
        <Text style={styles.floatingToggleText}>{viewMode === 'list' ? t('explore.mapView') : t('explore.listView')}</Text>
      </TouchableOpacity>

      {/* Filter Sheet Modal */}
      <FilterSheet
        visible={showFilterSheet}
        filters={filters}
        onApply={handleFilterApply}
        onClose={() => setShowFilterSheet(false)}
        trailCount={totalTrails}
        token={token}
        dogId={dogId}
        userLocation={locationCoords ?? null}
      />

      {/* Search Modal */}
      <Modal
        visible={showSearchModal}
        animationType="slide"
        presentationStyle="fullScreen"
        onRequestClose={handleCloseSearchModal}
      >
        <View style={[styles.searchModalContainer, { paddingTop: insets.top }]}>
          <View style={styles.searchModalHeader}>
            <TouchableOpacity style={styles.backButton} onPress={handleCloseSearchModal}>
              <ChevronLeft size={24} color="#5d6b4a" />
            </TouchableOpacity>
            <View style={styles.searchModalInputContainer}>
              <TextInput
                style={styles.searchModalInput}
                placeholder={t('explore.findTrailsOrLocations')}
                placeholderTextColor="#9ca3af"
                value={searchQuery}
                onChangeText={handleSearchQueryChange}
                autoFocus
                returnKeyType="search"
              />
              {searchQuery.length > 0 && (
                <TouchableOpacity style={styles.clearButton} onPress={() => setSearchQuery('')}>
                  <X size={20} color="#64748b" />
                </TouchableOpacity>
              )}
            </View>
          </View>

          <View style={styles.searchModalContent}>
            {/* Current location option (when not searching) */}
            {searchQuery.trim() === '' && (
              <TouchableOpacity style={styles.currentLocationItem} onPress={handleCurrentLocationPress}>
                <View style={styles.locationIconContainer}>
                  <Navigation size={20} color="#5d6b4a" />
                </View>
                <View style={styles.locationTextContainer}>
                  <Text style={styles.locationTitle}>{t('explore.currentLocation')}</Text>
                  <Text style={styles.locationSubtitle}>{currentLocation}</Text>
                </View>
              </TouchableOpacity>
            )}

            {/* Geocode error message */}
            {geocodeError && searchQuery.trim().length >= 2 && geocodeResults.length === 0 && (
              <View style={{ paddingHorizontal: 20, paddingVertical: 12 }}>
                <Text style={{ ...Typography.body(theme.textMuted), textAlign: 'center' }}>{geocodeError}</Text>
              </View>
            )}

            {/* Geocode results (locations) */}
            {geocodeResults.length > 0 && (
              <View style={{ marginBottom: 16 }}>
                <Text style={{ ...Typography.label(theme.textMuted), paddingHorizontal: 20, marginBottom: 8 }}>{t('explore.locations')}</Text>
                {geocodeResults.map((result, idx) => (
                  <TouchableOpacity
                    key={`geo-${idx}`}
                    style={styles.suggestionItem}
                    onPress={() => handleGeocodeResultPress(result)}
                  >
                    <View style={[styles.suggestionIconContainer, { backgroundColor: theme.accentPrimary + '20' }]}>
                      <MapPin size={20} color={theme.accentPrimary} />
                    </View>
                    <View style={styles.suggestionTextContainer}>
                      <Text style={styles.suggestionTitle} numberOfLines={2}>{result.display_name}</Text>
                      <Text style={styles.suggestionSubtitle}>📍 {result.place_type || t('explore.locationType')}</Text>
                    </View>
                  </TouchableOpacity>
                ))}
              </View>
            )}

            {/* Trail results */}
            {searchSuggestions.length > 0 && (
              <View>
                <Text style={{ ...Typography.label(theme.textMuted), paddingHorizontal: 20, marginBottom: 8 }}>{t('explore.trails')}</Text>
                <FlatList
                  data={searchSuggestions}
                  keyExtractor={(item) => item.id}
                  renderItem={({ item }) => (
                    <TouchableOpacity
                      style={styles.suggestionItem}
                      onPress={() => handleSuggestionPress(item.id)}
                    >
                      <View style={styles.suggestionIconContainer}>
                        <MapPin size={20} color="#64748b" />
                      </View>
                      <View style={styles.suggestionTextContainer}>
                        <Text style={styles.suggestionTitle}>{item.name}</Text>
                        <Text style={styles.suggestionSubtitle}>{item.location}</Text>
                      </View>
                    </TouchableOpacity>
                  )}
                  showsVerticalScrollIndicator={false}
                />
              </View>
            )}
          </View>
        </View>
      </Modal>
    </View>
  );
}